import {
  ColDef,
  ColumnApi,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  MenuItemDef,
  Module,
} from '@ag-grid-community/core'
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel'
import { MasterDetailModule } from '@ag-grid-enterprise/master-detail'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  Input,
  OnDestroy,
  OnInit,
  AfterViewInit,
  Output,
  EventEmitter,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  enableHeliosMultiRowDragSelection,
  heliosColumnTypes,
  heliosMultiRowSelectionConfig,
  heliosServerSideRowConfig,
} from '@helios/ag-grid-config'
import {
  CommentaryCountsService,
  CommentaryLiveAccountsService,
  CommentaryEntityRef,
  CommentaryEntitySearchService,
  CommentaryFollowService,
  CommentaryGridSelectionService,
  CommentCountCellRenderer,
  commentCountRendererKey,
  getCommentaryColumnIfAccessible,
  getCommentaryContextMenuItems,
  wireCommentaryCounts,
  refilterOnThreadChanges,
  wireCommentaryGrid,
  accountCounts,
} from '@helios/comments'
import { FilterModalService, RevertFilterService } from '@helios/filter-modal'
import {
  Account,
  AccountsRequestContext,
  AdvancedFilterContext,
  AggregateAccount,
  getSubAccountRowCountRequest,
  HighlightingService,
  portfolioAndSimplesColDefs,
} from '@helios/generic-components'
import {
  handleColumnVisibilityChange,
  toHeliosRequest,
} from '@helios/server-side-util'
import {
  BasicAccountInfo,
  MyAccountInfo,
  loadBreaksAction,
  PeriodLength,
  RootState,
  selectFocusedMyAccount,
  selectUserPolicy,
  setFocusedMyAccount,
  userHasCommentaryAccess,
  UserPolicy,
} from '@helios/state-management'
import { getYesterday } from '@helios/utils'
import { Store } from '@ngrx/store'
import { composeFieldPath, formatDate, ISODateFormat } from '@northfork/basics'
import { Subject } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  shareReplay,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators'
import { createFilterContextFromQuickSearch } from '../quick-search/quick-search-utils'
import { HttpAccountListService } from '../services/http-account-list.service'
import { AccountListGridService } from '../services/account-list-grid.service'
import {
  AccountListStatusBarModule,
  accountListStatusBarPanel,
} from '../status-bar/external-accounts-status-bar.module'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import { ExternalAccountsPanelKey } from '../../panel-key'
import { externalAccountCommentary } from '../external-accounts-commentary'
import { AgGridState } from '@northfork/ag-grid-angular'

export const IssuesType = 'IssuesType'
const subAccountListGridKey = `${ExternalAccountsPanelKey}-SubGrid`

type AggregateFilterType = 'aggregate' | 'sleeveAggregate'

const filterTypeLabels: Record<AggregateFilterType, string> = {
  aggregate: 'Aggregate',
  sleeveAggregate: 'Parent Portfolio',
}

const accountPath = composeFieldPath([] as Account[])

// Deliberately a subset of the Accounts panel's load options: My Accounts
// users get only these fixed windows, with no 2-year or custom from-date load.
const LOAD_WINDOW_OPTIONS: readonly PeriodLength[] = [
  PeriodLength.Last30Days,
  PeriodLength.Last100Days,
  PeriodLength.Last1Year,
]
const DEFAULT_LOAD_WINDOW = PeriodLength.Last100Days

@Component({
  selector: 'hs-sub-account-list-grid',
  templateUrl: './sub-account-list-grid.component.html',
  styleUrls: ['./sub-account-list-grid.component.scss'],
  providers: [FilterModalService, HighlightingService, RevertFilterService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubAccountListGridComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input()
  set actualReconDate(dateString: string | null) {
    if (dateString == null) {
      return
    }
    this.advancedFilterContext.reconDate = dateString
  }

  selectedAggregateAccount?: BasicAccountInfo
  filterType: AggregateFilterType = 'aggregate'
  focusedSimpleAccount?: MyAccountInfo

  get filterTypeLabel(): string {
    return filterTypeLabels[this.filterType]
  }

  advancedFilterContext: AdvancedFilterContext = {
    advancedFilters: {},
    reconDate: formatDate(getYesterday(), ISODateFormat) ?? '', // since getYesterday always returns date value so recon date won't be blank
  }

  private readonly destroySubject$ = new Subject<void>()

  readonly gridOptions$ = this.createGridOptions()

  quickSearch = new FormControl('')

  api?: GridApi
  columnApi?: ColumnApi

  hasAccessCommentary = false
  private commentaryWired = false

  readonly gridModules: Module[] = [
    MasterDetailModule,
    FiltersToolPanelModule,
    ServerSideRowModelModule,
    RangeSelectionModule,
    AccountListStatusBarModule,
  ]

  constructor(
    private readonly resolver: ComponentFactoryResolver,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly accountListService: HttpAccountListService,
    private readonly accountListGridService: AccountListGridService,
    private readonly store: Store<RootState>,
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly commentaryCountsService: CommentaryCountsService,
    private readonly commentaryLiveAccounts: CommentaryLiveAccountsService,
    private readonly commentaryFollowService: CommentaryFollowService,
    private readonly entitySearchService: CommentaryEntitySearchService,
    private readonly gridSelectionService: CommentaryGridSelectionService,
  ) {
    this.store
      .select(selectUserPolicy)
      .pipe(
        filter(policy => policy != undefined),
        take(1),
      )
      .subscribe(userPolicy => this.checkCommentaryAccess(userPolicy))
  }

  /**
   * DLITE-3912 — mirror the Accounts panel's commentary integration. Wire this
   * grid into the commentary drawer once the user has commentary access.
   * @internal
   */
  checkCommentaryAccess(userPolicy?: UserPolicy) {
    if (userPolicy && userHasCommentaryAccess(userPolicy)) {
      this.hasAccessCommentary = true
      this.wireCommentary()
    }
  }

  /**
   * Wire the portfolios/simples grid into the three commentary integration
   * points: follow-a-chip row focus, entity-link type-ahead, and
   * Pick-from-table. The row-node id is `accountId.toString()` (see
   * getRowNodeId), so focusGridRow looks the node up directly.
   * `filterWhenMissing: false` because an account chip is ambiguous — it may
   * belong to the Accounts panel or the sibling aggregates grid — so this grid
   * only focuses rows it already holds rather than filtering itself to an empty
   * result. Idempotent; torn down via destroySubject$.
   */
  private wireCommentary() {
    if (this.commentaryWired) return
    this.commentaryWired = true
    wireCommentaryGrid({
      follow: this.commentaryFollowService,
      search: this.entitySearchService,
      selection: this.gridSelectionService,
      panelKey: subAccountListGridKey,
      entityType: 'account',
      idColId: 'accountId',
      getApi: () => this.api,
      hasAccess: () => this.hasAccessCommentary,
      destroy$: this.destroySubject$,
      loadedRefs: () => this.collectLoadedAccountRefs(),
      selectedRefs: () => this.collectSelectedAccountRefs(),
      filterWhenMissing: false,
    })
  }

  private collectLoadedAccountRefs(): CommentaryEntityRef[] {
    const refs: CommentaryEntityRef[] = []
    this.api?.forEachNode(node => {
      const id = (node.data as Account)?.accountId
      if (id != null) refs.push({ type: 'account', id: id.toString() })
    })
    return refs
  }

  private collectSelectedAccountRefs(): CommentaryEntityRef[] {
    return (this.api?.getSelectedNodes() ?? [])
      .map(node => (node.data as Account)?.accountId)
      .filter((id): id is number => id != null)
      .map(id => ({ type: 'account' as const, id: id.toString() }))
  }

  ngOnInit() {
    this.quickSearch.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroySubject$),
      )
      .subscribe(() => {
        this.advancedFilterContext = createFilterContextFromQuickSearch(
          this.quickSearch.value,
          this.advancedFilterContext,
        )
        this.api?.onFilterChanged()
      })
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.api?.destroy()
  }

  ngAfterViewInit(): void {
    this.accountListGridService.selectedAggregateForFilter$
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(aggregateAccount => {
        this.setAggregateAccount(aggregateAccount, 'aggregate')
      })

    this.store
      .select(selectFocusedMyAccount)
      .pipe(
        takeUntil(this.destroySubject$),
        filter(account => !!account),
      )
      .subscribe(account => {
        this.focusedSimpleAccount = account
        this.api?.redrawRows()
      })
  }

  updateGridConfig() {
    void this.savedViewService.updatePanelSettingsOnly(subAccountListGridKey, {
      key: 'aggregateFilter',
      value: this.getAggregateFilterSetting(),
    })
  }

  getCurrentPanelSettingsObject() {
    return {
      aggregateFilter: this.getAggregateFilterSetting(),
    }
  }

  private getAggregateFilterSetting() {
    return {
      account: this.selectedAggregateAccount,
      type: this.filterType,
    }
  }

  private applyGridConfig(agGridStateSaved: AgGridState) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const panelSettings = agGridStateSaved.panelSettings as any
    if (!panelSettings) return

    if ('aggregateFilter' in panelSettings) {
      const saved = panelSettings.aggregateFilter
      const account = saved?.account as BasicAccountInfo | undefined
      const savedFilterType: AggregateFilterType =
        saved?.type === 'sleeveAggregate' ? 'sleeveAggregate' : 'aggregate'
      this.setAggregateAccount(account, savedFilterType)
    }
  }

  private setAggregateAccount(
    aggregateAccount?: BasicAccountInfo,
    filterType: AggregateFilterType = 'aggregate',
  ): void {
    const aggIds = aggregateAccount ? [aggregateAccount.accountId] : []

    this.advancedFilterContext.advancedFilters = {
      ...this.advancedFilterContext.advancedFilters,
      aggregateId: aggIds,
    }
    this.api?.onFilterChanged()
    this.selectedAggregateAccount = aggregateAccount
    this.filterType = aggregateAccount ? filterType : 'aggregate'
    this.changeDetector.detectChanges()
    if (this.selectedAggregateAccount) {
      this.updateGridConfig()
    }
  }

  private createDatasource(): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        try {
          const request = toHeliosRequest<
            AggregateAccount,
            AccountsRequestContext
          >(params, [], externalAccountCommentary.mandatoryFields(), {
            filterContext: {
              ...this.advancedFilterContext,
            },
          })

          this.accountListService
            .getPortfolioAndSimpleAccounts(request)
            .pipe(
              take(1),
              takeUntil(this.destroySubject$),
              tap(accounts => {
                const totalRows =
                  (params?.request?.startRow ?? 0) + accounts.length
                params.success({
                  rowData: accounts,
                  rowCount:
                    totalRows < (params?.request?.endRow ?? totalRows)
                      ? totalRows
                      : undefined,
                })
              }),
              tap(() => {}),
            )
            .subscribe()
        } catch (e) {
          params.fail()
        }
      },
    }
  }

  async createColumnDefs(): Promise<ColDef[]> {
    return [
      // DLITE-3912: pinned-left comment-count column; click opens the drawer
      // scoped to that account. Omitted entirely for users without commentary
      // access — see getCommentaryColumnIfAccessible.
      ...(await getCommentaryColumnIfAccessible(
        this.store,
        externalAccountCommentary,
        () => this.hasAccessCommentary,
        this.commentaryCountsService,
        accountCounts,
      )),
      ...portfolioAndSimplesColDefs(accountPath, this.getAccountsSetValues()),
    ]
  }

  async createGridOptions(): Promise<GridOptions> {
    return {
      columnDefs: await this.createColumnDefs(),
      components: {
        [commentCountRendererKey]: CommentCountCellRenderer,
      },
      // DLITE-3912: stable row-node id lets the commentary chip-follow focus a
      // loaded account row directly via api.getRowNode(accountId).
      getRowNodeId: ({ accountId }: Account) => accountId.toString(),
      ...heliosServerSideRowConfig,
      ...heliosMultiRowSelectionConfig,
      rowClassRules: {
        'loaded-account': params =>
          params.data?.accountId !== undefined &&
          this.focusedSimpleAccount?.accountId !== undefined &&
          params.data.accountId === this.focusedSimpleAccount.accountId,
      },
      columnTypes: {
        ...heliosColumnTypes,
      },
      serverSideDatasource: this.createDatasource(),
      statusBar: accountListStatusBarPanel,
      onRowDoubleClicked: event => {
        const account = event.data as BasicAccountInfo
        const myAccount = {
          accountId: account.accountId,
          accountName: account.accountName,
          isAggregate: false,
        }
        this.store.dispatch(setFocusedMyAccount({ account: myAccount }))
      },
      onColumnVisible: params =>
        handleColumnVisibilityChange(params, 'SubAccountList'),
      context: {
        resolver: this.resolver,
        changeDetector: this.changeDetector,
        getRowCountRequest: (api: GridApi) =>
          getSubAccountRowCountRequest(api, this.advancedFilterContext),
      },
      onGridReady: event => this.onGridReady(event),
      getContextMenuItems: params => this.getContextMenuItems(params),
    }
  }

  /**
   * @internal
   */
  async onGridReady(event: GridReadyEvent) {
    this.api = event.api
    this.columnApi = event.columnApi

    wireCommentaryCounts<Account>({
      changes$: this.commentaryCountsService.changes$,
      getApi: () => event.api,
      hasAccessForLoad: () => this.hasAccessCommentary,
      destroy$: this.destroySubject$,
      accountIdOf: row => row?.accountId,
      loadCounts: ids => this.commentaryCountsService.ensureAccountsLoaded(ids),
      live: { accounts: this.commentaryLiveAccounts, surface: 'total' },
    })
    refilterOnThreadChanges<Account>(() => event.api, this.destroySubject$, {
      accountIdOf: row => row?.accountId,
    })

    enableHeliosMultiRowDragSelection(event.api)
    enableHeliosCopyBehavior(event.api)

    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(event.api, DensityLevel.Compact === level),
      )

    const agGridStateSaved = await this.savedViewService.manageSavedView(
      event,
      subAccountListGridKey,
      this.destroySubject$,
      true,
      this.getCurrentPanelSettingsObject(),
    )

    this.applyGridConfig(agGridStateSaved)
  }

  private getAccountsSetValues() {
    return this.accountListService
      .getAccountsSetValues()
      .pipe(take(1), takeUntil(this.destroySubject$), shareReplay(2))
  }

  clearSelectedAggregateAccount() {
    this.accountListGridService.clearSelectedAggregateForFilter()
    this.updateGridConfig()
  }

  /**
   * Handles corner cases like if the user selects some rows and then right-clicks a row outside of that selection.
   * In that case you would expect to take the action against the row that was right-clicked, but ag-grid's selected
   * nodes will include the selection not the account right-clicked.
   */
  private getAccountsToTakeAccountAgainst(
    params: GetContextMenuItemsParams,
  ): Account[] {
    const account: Account | undefined = params.node?.data
    if (account == null) return []
    const selectedAccounts: Account[] = params.api
      .getSelectedNodes()
      .map(node => node.data)

    if (
      selectedAccounts.length == 0 ||
      !selectedAccounts.some(
        selectedAccount => selectedAccount.accountId === account.accountId,
      )
    ) {
      return [account]
    }

    return selectedAccounts
  }

  getContextMenuItems(
    params: GetContextMenuItemsParams,
  ): (string | MenuItemDef)[] {
    const accounts: Account[] = this.getAccountsToTakeAccountAgainst(params)

    const items: (string | MenuItemDef)[] = [
      {
        name: `Load Breaks`,
        action: () =>
          this.store.dispatch(
            loadBreaksAction({
              type: 'accountIds',
              ids: accounts.map(a => a.accountId),
            }),
          ),
      },
      this.getLoadTransactionsMenuItem(accounts),
    ]

    const sharedParentId = this.getSharedParentPortfolioId(accounts)
    if (sharedParentId != null) {
      const parentAccount = accounts.find(a => a.accountId === sharedParentId)
      const parentName = parentAccount?.accountName ?? String(sharedParentId)
      items.push({
        name: 'Filter by sleeve portfolio',
        tooltip:
          'Filters the account list to include the parent portfolio & all sub-sleeves.',
        action: () => {
          this.clearQuickSearch()
          this.setAggregateAccount(
            { accountId: sharedParentId, accountName: parentName },
            'sleeveAggregate',
          )
        },
      })
    }

    // DLITE-3912: View / Add Comment items for the right-clicked account,
    // mirroring the Accounts panel. Returns [] without commentary access.
    items.push(
      ...getCommentaryContextMenuItems(
        this.store,
        this.hasAccessCommentary,
        externalAccountCommentary,
        params.node?.data,
      ),
    )

    return items
  }

  /**
   * Only one account can be loaded at a time, so a multi-row selection gets a
   * flat disabled item with no `action` and no `subMenu`: nothing beneath it
   * can fire, whatever ag-grid does with `disabled` on a parent item.
   */
  private getLoadTransactionsMenuItem(accounts: Account[]): MenuItemDef {
    const name = 'Load Transactions & Positions'
    if (accounts.length !== 1) {
      return {
        name,
        disabled: true,
        tooltip: 'Only one account can be loaded at a time',
      }
    }

    const [account] = accounts
    const load = (periodLength: PeriodLength) => () =>
      this.loadAccount(account.accountId, false, account.isLIB, periodLength)

    return {
      name,
      tooltip: `Load Transactions and Positions for account ${account.accountId}.`,
      // Clicking the parent item loads the default window; hovering opens
      // the submenu to pick a different one.
      action: load(DEFAULT_LOAD_WINDOW),
      subMenu: LOAD_WINDOW_OPTIONS.map(periodLength => ({
        name: periodLength,
        action: load(periodLength),
      })),
    }
  }

  private getSharedParentPortfolioId(accounts: Account[]): number | undefined {
    if (accounts.length === 0) return undefined
    const parentIds: number[] = []
    for (const account of accounts) {
      if (account.accountType === 'sleeveAggregate') {
        parentIds.push(account.accountId)
      } else if (
        account.accountType === 'sleeveSimple' &&
        account.sleeveAggregateId != null
      ) {
        parentIds.push(account.sleeveAggregateId)
      } else {
        // Non-sleeve account, or sleeveSimple without a parent — disqualifies
        // the whole selection from a sleeve-portfolio filter.
        return undefined
      }
    }
    return new Set(parentIds).size === 1 ? parentIds[0] : undefined
  }

  private clearQuickSearch() {
    this.quickSearch.setValue('', { emitEvent: false })
    this.advancedFilterContext = createFilterContextFromQuickSearch(
      '',
      this.advancedFilterContext,
    )
  }

  @Output() onLoadAccount = new EventEmitter<{
    accountIds: number[]
    reconDate: Date
    withModal: boolean
    isLIBAccount: boolean
    periodLength: PeriodLength
  }>()

  /*@internal*/
  loadAccount(
    accountId: number,
    withModal: boolean,
    isLIBAccount: boolean,
    periodLength: PeriodLength,
  ) {
    this.onLoadAccount.emit({
      accountIds: [accountId],
      reconDate:
        (this.advancedFilterContext.reconDate as unknown as Date) ?? new Date(),
      withModal,
      isLIBAccount,
      periodLength,
    })
  }
}
